"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.default = exports.Checkbox = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Checkbox Component
 * @param {CheckboxProps} props - React properties passed from composition
 * @returns JSX.Element
*/

const Checkbox = props => {
  const {
    id,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    className,
    title,
    description,
    count,
    screenreaderText,
    inputRef,
    checked,
    children,
    onFocus,
    onBlur,
    onChange,
    checkboxContainerWidth,
    isFullyClickable = defaultProps.isFullyClickable,
    variant = defaultProps.variant,
    labelPosition = defaultProps.labelPosition,
    disabled = defaultProps.disabled,
    size = defaultProps.size,
    checkboxVerticalPosition = defaultProps.checkboxVerticalPosition,
    ...other
  } = props;
  const customWidthStyle = checkboxContainerWidth && {
    '--customWidth': `${checkboxContainerWidth}`
  };
  const labelCustomClass = (0, _classnames.default)(`pal-c-Checkbox__postion-${checkboxVerticalPosition}`, {
    'pal-c-Checkbox__customWidth': checkboxContainerWidth
  });
  const componentClassName = (0, _classnames.default)('pal-c-Checkbox', className, `pal-c-Checkbox--${labelPosition}`, {
    'pal-c-Checkbox--checked': checked,
    'pal-c-Checkbox--disabled': disabled,
    'pal-c-Checkbox--fullyClickable': isFullyClickable,
    'pal-c-Checkbox--with-default-content': Boolean(title || description || count),
    'pal-c-Checkbox--with-children': Boolean(children),
    'pal-c-Checkbox__postion-top': !isFullyClickable && checkboxVerticalPosition === 'top',
    'pal-c-Checkbox__postion-center': !isFullyClickable && checkboxVerticalPosition === 'center',
    'pal-c-Checkbox__postion-bottom': !isFullyClickable && checkboxVerticalPosition === 'bottom',
    'pal-c-Checkbox--toggle': variant === 'toggle'
  });
  const handleChange = e => {
    if (!disabled) {
      onChange?.(e);
    }
  };
  const defaultContentPassed = title || description || count || screenreaderText;
  const showContentContainer = children || defaultContentPassed;
  const renderCheckboxDefaultContent = defaultContentPassed ? /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Checkbox__content--default"
  }, (title || count) && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Checkbox__title__container"
  }, title && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    className: "pal-c-Checkbox__title",
    textStyle: size === 'compact' ? 'body' : 'body-lg'
  }, title), count && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    className: "pal-c-Checkbox__count",
    textStyle: "body-sm"
  }, count)), description && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    className: "pal-c-Checkbox__description",
    textStyle: size === 'compact' ? 'body-sm' : 'body'
  }, description), screenreaderText && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    className: "sr-only"
  }, screenreaderText)) : null;
  const renderCheckboxChildren = children ? /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Checkbox__content--children"
  }, children) : null;
  const renderCheckmark = /*#__PURE__*/_react.default.createElement("span", {
    "aria-hidden": "true",
    className: "pal-c-Checkbox__icon"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    focusable: false,
    name: "check",
    size: "custom"
  }));
  const renderInput = /*#__PURE__*/_react.default.createElement("input", _extends({
    ref: inputRef,
    type: "checkbox",
    id: id,
    className: "pal-c-Checkbox__input",
    checked: checked,
    disabled: disabled,
    onChange: handleChange,
    onFocus: onFocus,
    onBlur: onBlur,
    "aria-checked": checked,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy
  }, other));
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: customWidthStyle
  }, isFullyClickable && /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: id,
    "aria-label": ariaLabel,
    className: labelCustomClass
  }, renderInput, renderCheckmark, showContentContainer && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Checkbox__content"
  }, renderCheckboxDefaultContent, renderCheckboxChildren)), !isFullyClickable && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: id,
    "aria-label": ariaLabel
  }, renderInput, renderCheckmark), showContentContainer && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Checkbox__content"
  }, renderCheckboxDefaultContent, renderCheckboxChildren)));
};

/**
 * Property type definitions
 * @typedef CheckboxProps
 * @type {object}
 * @property {string} id - Sets id for the Checkbox input
 * @property {boolean} checked - Sets checked state of the Checkbox
 * @property {boolean} disabled - Sets the disabled state of the Checkbox
 * @property {function} onChange - Sets the onChange when this function is called
 * @property {function} onFocus - Sets the onFocus when this function is called
 * @property {function} onBlur - Sets the onBlur when this function is called
 * @property {boolean} isFullyClickable - Sets if clicking on the custom children checks the Checkbox
 * @property {node} children - Custom children for the Checkbox
 * @property {string} ariaLabel - Sets aria-label for the input
 * @property {string} ariaLabelledBy - Sets aria-labelledby for the input
 * @property {string} ariaDescribedBy - Sets aria-describedBy for the input
 * @property {string} className - Additional class names for the Checkbox
 * @property {string} variant - Sets the variant of the Checkbox
 * @property {string} labelPosition - Sets the label position of the Checkbox
 * @property {any} inputRef - Reference to the Checkbox
 * @property {string} title - Sets the title text of the Checkbox
 * @property {number} count - Sets the count of the Checkbox
 * @property {string} description - Sets the description of the Checkbox
 * @property {string} screenreaderText - Sets the screenreader text of the Checkbox
 * @property {string} size - Sets the size of the Checkbox
 * @property {string} checkboxContainerWidth - Sets the width of the container of the Checkbox
 * @property {string} checkboxVerticalPosition - Sets the position of the Checkbox as top / center / bottom
 */
exports.Checkbox = Checkbox;
const propTypes = exports.propTypes = {
  /**
   * HTML ID for the checkbox input (required)
   */
  id: _propTypes.default.string.isRequired,
  /**
   * Whether the checkbox is checked
   */
  checked: _propTypes.default.bool,
  /**
   * Whether the checkbox is disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Callback when checkbox state changes
   */
  onChange: _propTypes.default.func.isRequired,
  /**
   * Callback when input receives focus
   */
  onFocus: _propTypes.default.func,
  /**
   * Callback when input loses focus
   */
  onBlur: _propTypes.default.func,
  /**
   * Whether the entire label and content area is clickable
   */
  isFullyClickable: _propTypes.default.bool,
  /**
   * Custom children node inside checkbox content
   */
  children: _propTypes.default.node,
  /**
   * ARIA label
   */

  ariaLabel: _propTypes.default.string,
  /**
   * ARIA label reference ID
   */
  ariaLabelledBy: _propTypes.default.string,
  /**
   * ARIA description reference ID
   */
  ariaDescribedBy: _propTypes.default.string,
  /**
   * Additional class names for the checkbox component
   */
  className: _propTypes.default.string,
  /**
   * Checkbox visual style variant
   * - 'default': standard checkbox
   * - 'toggle': switch-like checkbox
   */
  variant: _propTypes.default.oneOf(['default', 'toggle']),
  /**
   * Position of the label relative to checkbox
   * - 'left': label before checkbox
   * - 'right': label after checkbox
   */
  labelPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Title text next to the checkbox
   */
  title: _propTypes.default.string,
  /**
   * Optional description shown under the title
   */
  description: _propTypes.default.string,
  /**
   * Count value shown next to the title (can be number or string)
   */
  count: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  /**
   * Screenreader-only text for accessibility
   */
  screenreaderText: _propTypes.default.string,
  /**
   * inputRef - Reference to the Checkbox
  */
  inputRef: _propTypes.default.any,
  /**
   * size - Sets the size of the Checkbox
   * - 'default': default font sizes for checkbox content
   * - 'compact': compact font sizes for checkbox content
   */
  size: _propTypes.default.oneOf(['default', 'compact']),
  /**
   * Sets the width of the container of the Checkbox
   */
  checkboxContainerWidth: _propTypes.default.string,
  /**
   * Sets the width of the container of the Checkbox
   */
  checkboxVerticalPosition: _propTypes.default.oneOf(['top', 'center', 'bottom'])
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} isFullyClickable - Default: true
 * @property {string} variant - Default: 'default'
 * @property {string} labelPosition - Default: 'left'
 * @property {boolean} disabled - Default: false
 * @property {string} size - Default: 'default'
 * @property {string} checkboxVerticalPosition - Default: 'center'
 */
const defaultProps = {
  isFullyClickable: true,
  variant: 'default',
  labelPosition: 'left',
  disabled: false,
  size: 'default',
  checkboxVerticalPosition: 'center'
};
Checkbox.propTypes = propTypes;
var _default = exports.default = Checkbox;